import { useMutation } from '@apollo/client';
import type { i18n } from 'i18next';
import { type FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import {
    Alert,
    Body1,
    Button,
    Flex,
    FullBleed,
    Heading1,
    Image,
    RichText,
    Stack,
    Subtitle1,
} from '@myphx-engineering/phx-design-system';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';

import { drugDescriptionMinWidth } from '../../../constants';
import {
    useDrugProgram,
    useMedicationConfigOverride,
} from '../../../contexts/DrugProgramProvider';
import { justPlacedOnHoldVar } from '../../../contexts/fill';
import { useFillPrescription } from '../../../contexts/fill/use-fill-prescription';
import {
    CancelPrescriberOrderModificationDocument,
    type AuthorizedMedicationInfoFragment,
    type DigitallyReceivedPrescriberOrderInfoFragment,
    type UnknownMedicationInfoFragment,
} from '../../../graphql/generated/graphql';
import type {
    MedicationConfigOverride,
    MedicationOverride,
} from '../../../hooks/use-config-api';
import { usePatientContext } from '../../../hooks/use-patient-context';
import { useRefetchQueries } from '../../../hooks/use-refetch-queries';
import type { DrugConfig } from '../../../types/drug-config';
import {
    getDrugConfigByMedication,
    getDrugConfigByNdc,
} from '../../../util/config/get-drug-config-by-medication';
import { formatMedicationQuantity } from '../../../util/formatting/prescriber-order';
import { getProductDescription } from '../../../util/formatting/prescriber-order/get-product-description';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';
import { getMedicationNdc } from '../../../util/get-medication-ndc';
import {
    getNdcImage,
    getNdcImageMaxDimensions,
    getNdcImageOrientation,
} from '../../../util/get-ndc-image';
import { getRefetchQueryForPrescription } from '../../../util/get-refetch-query-for-prescription';
import { getChangeRequestedNDC } from '../../../util/orders/get-change-requested-ndc';
import {
    getSwitchDeviceDescriptions,
    resolveReasonContent,
} from '../../../util/orders/hold-screen-content';

import { ConfirmUnholdModal } from './ConfirmUnholdModal';

type DrugDisplayData = {
    description: string | null | undefined;
    quantity: string;
    strength: string;
    image: string;
    imageOrientation: ReturnType<typeof getNdcImageOrientation>;
};

type GetDataFromDrxInput = {
    drx: DigitallyReceivedPrescriberOrderInfoFragment;
    medication:
        | AuthorizedMedicationInfoFragment
        | UnknownMedicationInfoFragment;
    medicationOverride: MedicationOverride;
    ndc: string;
    drugConfig: DrugConfig;
    configOverride: MedicationConfigOverride;
    i18n: i18n;
};

const getDataFromDrx = ({
    drx,
    medication,
    medicationOverride,
    ndc,
    drugConfig,
    configOverride,
    i18n,
}: GetDataFromDrxInput): DrugDisplayData => {
    const description = getProductDescription(drx, i18n, medicationOverride);
    const quantity = formatMedicationQuantity(
        drx.quantity,
        medication,
        medicationOverride
    );

    const strength = `${medicationOverride.strengthValue || ''}${medicationOverride.strengthUnit || ''}`;
    const image = getNdcImage(drugConfig, ndc!, configOverride);
    const imageOrientation = getNdcImageOrientation(ndc!, configOverride);
    return { description, quantity, strength, image, imageOrientation };
};

type GetDataFromRequestedMedicationInput = {
    medication: MedicationOverride;
    ndc: string;
    drugConfig: DrugConfig;
    configOverride: MedicationConfigOverride;
};
const getDataFromRequestedMedication = ({
    medication,
    ndc,
    drugConfig,
    configOverride,
}: GetDataFromRequestedMedicationInput): DrugDisplayData => {
    const description = medication.description;
    const quantity =
        `${medication.packageQuantity || ''} ${medication.packageSizeUnit || ''}`.trim();
    const strength = `${medication.strengthValue || ''}${medication.strengthUnit || ''}`;
    const image = getNdcImage(drugConfig, ndc, configOverride);
    const imageOrientation = getNdcImageOrientation(ndc, configOverride);

    return { description, quantity, strength, image, imageOrientation };
};

export const OnHoldScreen: FC = () => {
    const { t, i18n } = useTranslation('lilly');

    const { drx, drxId, medication } = useFillPrescription();
    const ndc = getMedicationNdc(medication);
    const medConfig = useMedicationConfigOverride();
    const { patient: userPatient } = usePatientContext();

    const drugProgram = useDrugProgram();
    const configOverride = useMedicationConfigOverride();
    const drugConfig = getDrugConfigByMedication(drugProgram, medication);
    const medicationOverride = ndc ? configOverride.ndcInfo[ndc] : null;

    const onHoldConfig = ndc ? (medConfig.ndcHoldConfigs?.[ndc] ?? null) : null;

    const requestedNdc = getChangeRequestedNDC(drx);

    const requestedMedication = requestedNdc
        ? configOverride.ndcInfo[requestedNdc]
        : null;

    const requestedDrugConfig = getDrugConfigByNdc(
        drugProgram,
        requestedNdc ?? undefined
    );

    const isChangeRequest =
        !!requestedNdc && !!requestedMedication && !!requestedDrugConfig;

    const changeRequestReason = drx.changeRequestDetails?.reason;
    const deviceDescriptions = getSwitchDeviceDescriptions(
        ndc ? medConfig.ndcSwitchConfigsV2?.[ndc] : undefined,
        requestedNdc
    );

    const { refetch, isLoading: isRefetchLoading } = useRefetchQueries(
        getRefetchQueryForPrescription(drxId, userPatient.id)
    );

    const [CancelChangeRequest, { loading: isCancelLoading }] = useMutation(
        CancelPrescriberOrderModificationDocument
    );

    const handleConfirm = useCallback(async () => {
        await CancelChangeRequest({
            variables: {
                prescriberOrderId: drxId,
            },
        });

        await refetch();
    }, [CancelChangeRequest, drxId, refetch]);

    const isLoading = isRefetchLoading || isCancelLoading;

    const [modalOpened, { open: openModal, close: closeModal }] =
        useDisclosure(false);

    useEffect(() => {
        justPlacedOnHoldVar(false);
    }, []);

    if (
        !onHoldConfig ||
        !drugConfig ||
        !medicationOverride ||
        drx.status !== 'ON_HOLD'
    ) {
        return (
            <Navigate
                replace
                to={{
                    pathname: getAbsoluteRoute('fill.drxId.root', { drxId }),
                }}
            />
        );
    }

    const confirmModalConfig = onHoldConfig.holdScreen.confirmModal ?? null;
    const confirmModal = confirmModalConfig
        ? {
              ...confirmModalConfig,
              body:
                  resolveReasonContent(
                      confirmModalConfig.body,
                      changeRequestReason
                  ) ?? [],
          }
        : null;

    const { description, quantity, strength, image, imageOrientation } =
        isChangeRequest
            ? getDataFromRequestedMedication({
                  ndc: requestedNdc,
                  medication: requestedMedication,
                  drugConfig: requestedDrugConfig,
                  configOverride,
              })
            : getDataFromDrx({
                  drx,
                  ndc: ndc!,
                  configOverride,
                  drugConfig,
                  medicationOverride,
                  medication,
                  i18n,
              });

    const { maxHeight, maxWidth } = getNdcImageMaxDimensions(imageOrientation);

    const config = onHoldConfig.holdScreen;
    const pageTitle = isChangeRequest
        ? (config.title?.withChangeReq ?? config.title?.default)
        : config.title?.default;
    const body = isChangeRequest
        ? (resolveReasonContent(
              config.body.withChangeReq,
              changeRequestReason
          ) ?? config.body.default)
        : config.body.default;

    return (
        <>
            <Stack gap="lg" data-testid="on-hold-page-root">
                <FullBleed
                    color={config.alert.color}
                    spacing="none"
                    breakpoint="sm"
                >
                    <Alert
                        severity={config.alert.color}
                        data-testid="on-hold-alert"
                    >
                        {config.alert.body.map(({ key, ...line }) => (
                            <RichText key={key} {...line} />
                        ))}
                    </Alert>
                </FullBleed>
                <Flex
                    direction={
                        imageOrientation === 'portrait' ? 'row' : 'column'
                    }
                    justify={
                        imageOrientation === 'portrait'
                            ? 'space-between'
                            : undefined
                    }
                    wrap={imageOrientation === 'portrait' ? 'wrap' : undefined}
                    gap="xxs"
                >
                    <Stack gap="xxs" miw={drugDescriptionMinWidth} flex={1}>
                        {pageTitle ? <Heading1>{pageTitle}</Heading1> : null}
                        <Subtitle1 data-testid="on-hold-drug-name">
                            {description}
                        </Subtitle1>
                        <Body1>{t('order.quantity', { quantity })}</Body1>
                        {!!strength && (
                            <Body1>{t('order.strength', { strength })}</Body1>
                        )}
                    </Stack>
                    <Flex w="auto">
                        <Image
                            src={image}
                            aria-hidden="true"
                            fit="contain"
                            maw={maxWidth}
                            mah={maxHeight}
                            w="auto"
                        />
                    </Flex>
                </Flex>
                <Stack data-testid="on-hold-body-copy">
                    {body.map(({ key, ...line }) => (
                        <RichText
                            key={key}
                            {...line}
                            values={deviceDescriptions}
                        />
                    ))}
                </Stack>
                <Stack>
                    <Button
                        disabled={isLoading}
                        link
                        to={getAbsoluteRoute('home')}
                        data-testid="on-hold-return-home-button"
                    >
                        {config.buttonLabels.home}
                    </Button>
                    <Button
                        loading={isLoading}
                        variant="outline"
                        onClick={
                            isChangeRequest && confirmModal
                                ? openModal
                                : handleConfirm
                        }
                        data-testid="on-hold-continue-current-prescription-button"
                    >
                        {config.buttonLabels.unhold}
                    </Button>
                </Stack>
            </Stack>
            {confirmModal && (
                <ConfirmUnholdModal
                    opened={modalOpened}
                    onClose={closeModal}
                    onConfirm={handleConfirm}
                    isLoading={isLoading}
                    config={confirmModal}
                    values={deviceDescriptions}
                />
            )}
        </>
    );
};
